'use client';

import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type FC,
} from 'react';
import type { SelectUserProps } from './SelectUser.types';
import { Icon, Heading, Flex, type ResultListItem } from '@/_components';
import { CompanyContext } from '@/_contexts/admin/CompanyProvider';
import { User } from '@/_types/models/user';
import styles from './SelectUser.module.css';
import { InputChips } from '@/_components/InputChips';
import { useDebounce } from '@/_hooks/useDebounce';
import { findUsers } from '@/_api/v2/admin-users';
import { UserContext } from '@/_contexts/UserProvider';

export const SelectUser: FC<SelectUserProps> = ({ onClose }) => {
  const { token } = useContext(UserContext);
  const { selectedCompany, setSelectedUser, onToggleAddClient } =
    useContext(CompanyContext);

  const [chips, setChips] = useState<Set<string>>(new Set());
  const [chipText, setChipText] = useState<string>('');
  const [users, setUsers] = useState<Array<User>>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const debouncedSearch = useDebounce(chipText, 300);

  const fetchUsers = useCallback(
    async (search: string): Promise<void> => {
      if (!search || !token) return setUsers([]);

      setIsLoading(true);

      try {
        const users = await findUsers({ token, search });
        if (Array.isArray(users)) setUsers(users);
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    },
    [token],
  );

  const onChipText = (event: React.ChangeEvent<HTMLInputElement>) => {
    setChipText(event.target.value);
  };

  const userItems: Array<ResultListItem> = useMemo(
    () =>
      users
        ?.filter((user) => !user?.is_advisor)
        ?.map((user) => ({
          name:
            user?.lname && user?.fname
              ? `${user.lname}, ${user.fname}`
              : user.full_name,
          value: user?.id?.toString() || '-1',
        })),
    [users],
  );

  const onSelectCreateUser = () => {
    onClose();
    onToggleAddClient();
  };

  const results: Array<ResultListItem> = useMemo(() => {
    return userItems?.map((user) => ({
      name: user.name,
      value: user.value,
    }));
  }, [userItems]);

  useEffect(() => {
    fetchUsers(debouncedSearch);
  }, [fetchUsers, debouncedSearch]);

  useEffect(() => {
    if (chips.size === 1) {
      const chipValue = chips.values().next().value;

      if (chipValue !== undefined) {
        const newUser: Partial<User> | undefined = users.find(
          (c) => c.id === parseInt(chipValue, 10),
        );

        return setSelectedUser(newUser as User);
      }
    }
  }, [chips]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Flex isColumn={true} gap={8}>
      {selectedCompany?.id && (
        <Heading level={5}>
          Which user would you like to add to &ldquo;
          {selectedCompany?.name}&rdquo;?
        </Heading>
      )}
      <Flex isYCentered={true} gap={4} isFullWidth={true}>
        <Flex
          isColumn={true}
          isCentered={true}
          isFullWidth={true}
          className={styles.selectUser__chooseNewEntityItem}
        >
          <InputChips
            results={results}
            isLoading={isLoading}
            chips={chips}
            label="Existing User"
            onChange={onChipText}
            setChips={setChips}
            placeholder="Doe, John"
            value={chipText}
          />
        </Flex>
        <Flex
          isCentered={true}
          gap={2}
          onClick={onSelectCreateUser}
          isFullWidth={true}
          className={styles.selectUser__chooseNewEntityItem}
        >
          <Icon size={24} color="accent" name="user-plus" />
          <Heading color="accent" level={6}>
            New User
          </Heading>
        </Flex>
      </Flex>
    </Flex>
  );
};
